import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Upload, Sparkles, RefreshCw, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DuplicateSuspectDialog from "./DuplicateSuspectDialog";

/**
 * Fase 7.9 · Aggiungi vino dalla FOTO della bottiglia.
 *
 * Flusso:
 *  1. L'admin scatta / carica la foto FRONTE (obbligatoria) e opzionale RETRO.
 *  2. Bottone "Analizza foto" → chiama /admin/wines/from-photo/analyze.
 *  3. Form precompilato editabile con anteprima foto.
 *  4. Bottone "Crea vino" → chiama /admin/wines/from-photo/create.
 *  5. Redirect alla scheda del vino appena creato.
 */
export default function AdminWineFromPhoto() {
    const nav = useNavigate();
    const [frontFile, setFrontFile] = useState(null);
    const [frontPreview, setFrontPreview] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [backPreview, setBackPreview] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [creating, setCreating] = useState(false);
    const [extracted, setExtracted] = useState(null);
    const [form, setForm] = useState(null);
    const [dupDialog, setDupDialog] = useState({ open: false, candidates: [] });
    const frontInputRef = useRef(null);
    const backInputRef = useRef(null);

    const handleFile = (which, file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (which === "front") {
            setFrontFile(file);
            setFrontPreview(url);
        } else {
            setBackFile(file);
            setBackPreview(url);
        }
    };

    const analyze = async () => {
        if (!frontFile) {
            toast.error("Carica prima la foto FRONTE");
            return;
        }
        setAnalyzing(true);
        try {
            const fd = new FormData();
            fd.append("front", frontFile);
            if (backFile) fd.append("back", backFile);
            const { data } = await api.post("/admin/wines/from-photo/analyze", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90_000,
            });
            const ex = data.extracted || {};
            setExtracted(ex);
            setForm({
                name: ex.name || "",
                producer: ex.producer || "",
                vintage: ex.vintage != null ? String(ex.vintage) : "",
                type: ex.type || "rosso",
                country: ex.country || "Italia",
                region: ex.region || "",
                provincia: "",
                denomination: ex.denomination || "",
                grapes_csv: Array.isArray(ex.grapes) ? ex.grapes.join(", ") : "",
                size: ex.size || "0.75",
                base_price_public: "",
                tasting_notes: "",
                pairing: "",
                description: "",
            });
            toast.success(`Estrazione completata · confidenza ${ex.confidence || "media"}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore analisi foto");
        } finally {
            setAnalyzing(false);
        }
    };

    const create = async (ackDuplicates = false) => {
        if (!form || !frontFile) return;
        if (!form.name?.trim() || !form.producer?.trim()) {
            toast.error("Nome e produttore sono obbligatori");
            return;
        }
        setCreating(true);
        try {
            const fd = new FormData();
            fd.append("front", frontFile);
            Object.entries(form).forEach(([k, v]) => {
                if (v !== null && v !== undefined) fd.append(k, String(v));
            });
            fd.append("ack_duplicates", ackDuplicates ? "true" : "false");
            const { data } = await api.post("/admin/wines/from-photo/create", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60_000,
            });
            toast.success(
                data?.pipeline_enqueued
                    ? "Vino creato · arricchimento AI in corso (verrà in Segnalazioni)"
                    : "Vino creato dalla foto"
            );
            nav(`/admin/vini/${data.id}`);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409
                && detail && typeof detail === "object"
                && detail.reason === "duplicate_candidates") {
                setDupDialog({ open: true, candidates: detail.candidates || [] });
                setCreating(false);
                return;
            }
            toast.error(formatApiErrorDetail(detail) || "Errore creazione");
        } finally {
            setCreating(false);
        }
    };

    const proceedFromDialog = () => {
        setDupDialog({ open: false, candidates: [] });
        create(true);
    };

    const reset = () => {
        setFrontFile(null); setFrontPreview(null);
        setBackFile(null); setBackPreview(null);
        setExtracted(null); setForm(null);
    };

    return (
        <div className="max-w-4xl mx-auto" data-testid="admin-photo-page">
            <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
                <h1 className="text-2xl flex items-center gap-2">
                    <Camera className="w-5 h-5" strokeWidth={1.5} />
                    Aggiungi vino dalla foto
                </h1>
                {(frontFile || form) && (
                    <button
                        type="button"
                        onClick={reset}
                        className="co-btn-ghost text-sm"
                        data-testid="photo-reset-btn"
                    >
                        <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                        Ricomincia
                    </button>
                )}
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
                Terzo metodo di popolamento (oltre a gestionale Mexal e file
                PDF/Excel/CSV): scatta o carica la foto <b>fronte</b> della
                bottiglia (obbligatoria) e opzionalmente il <b>retro</b>. L'AI
                estrae i dati leggibili in etichetta. Rivedi il form
                precompilato e conferma per creare la scheda. I vini creati
                da foto restano fuori dalla sincronizzazione Mexal.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
                {/* Front upload */}
                <PhotoSlot
                    label="Fronte (obbligatoria)"
                    testId="photo-front"
                    file={frontFile}
                    preview={frontPreview}
                    inputRef={frontInputRef}
                    onFile={(f) => handleFile("front", f)}
                    required
                />
                <PhotoSlot
                    label="Retro (opzionale)"
                    testId="photo-back"
                    file={backFile}
                    preview={backPreview}
                    inputRef={backInputRef}
                    onFile={(f) => handleFile("back", f)}
                />
            </div>

            <div className="mt-5">
                <button
                    type="button"
                    onClick={analyze}
                    disabled={!frontFile || analyzing}
                    className="co-btn-primary"
                    data-testid="photo-analyze-btn"
                >
                    <Sparkles className={`w-4 h-4 ${analyzing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {analyzing ? "Analisi in corso…" : "Analizza foto"}
                </button>
            </div>

            {form && (
                <ExtractedForm
                    form={form}
                    setForm={setForm}
                    extracted={extracted}
                    onCreate={() => create(false)}
                    creating={creating}
                />
            )}

            <DuplicateSuspectDialog
                open={dupDialog.open}
                onOpenChange={(v) => setDupDialog((s) => ({ ...s, open: v }))}
                candidates={dupDialog.candidates}
                onProceed={proceedFromDialog}
                onCancel={() => setDupDialog({ open: false, candidates: [] })}
            />
        </div>
    );
}

function PhotoSlot({ label, testId, file, preview, inputRef, onFile, required }) {
    return (
        <div
            className="border border-dashed border-[hsl(var(--border))] rounded-sm p-4 bg-white flex flex-col"
            data-testid={`${testId}-slot`}
        >
            <div className="text-sm font-medium mb-3">
                {label}
                {required && <span className="text-[hsl(var(--primary))] ml-1">*</span>}
            </div>
            {preview ? (
                <img
                    src={preview}
                    alt={label}
                    className="w-full aspect-[3/4] object-contain bg-[#F5EFE7] rounded-sm mb-3"
                    data-testid={`${testId}-preview`}
                />
            ) : (
                <div className="w-full aspect-[3/4] flex items-center justify-center bg-[#F5EFE7]/60 rounded-sm mb-3 text-[hsl(var(--muted-foreground))]">
                    <Camera className="w-10 h-10 opacity-40" strokeWidth={1} />
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid={`${testId}-input`}
            />
            <div className="flex gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => {
                        // Attempt native camera; falls back to file picker if no camera
                        if (inputRef.current) {
                            inputRef.current.setAttribute("capture", "environment");
                            inputRef.current.click();
                        }
                    }}
                    className="co-btn-ghost text-xs"
                    data-testid={`${testId}-capture-btn`}
                >
                    <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Scatta
                </button>
                <button
                    type="button"
                    onClick={() => {
                        if (inputRef.current) {
                            inputRef.current.removeAttribute("capture");
                            inputRef.current.click();
                        }
                    }}
                    className="co-btn-ghost text-xs"
                    data-testid={`${testId}-upload-btn`}
                >
                    <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Carica
                </button>
            </div>
        </div>
    );
}

function ExtractedForm({ form, setForm, extracted, onCreate, creating }) {
    const upd = (k, v) => setForm({ ...form, [k]: v });
    return (
        <div className="mt-8 bg-white border border-[hsl(var(--border))] rounded-sm p-5" data-testid="photo-form">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-lg">Dati estratti (modificabili)</h2>
                {extracted?.confidence && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#F4E9DC] text-[#5B1C2C] border border-[#D9B98F]">
                        Confidenza: {extracted.confidence}
                    </span>
                )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Nome" required>
                    <Input value={form.name} onChange={(e) => upd("name", e.target.value)} className="bg-white" data-testid="photo-field-name" />
                </FormField>
                <FormField label="Produttore" required>
                    <Input value={form.producer} onChange={(e) => upd("producer", e.target.value)} className="bg-white" data-testid="photo-field-producer" />
                </FormField>
                <FormField
                    label="Annata"
                    hint="Solo se stampata in etichetta — non dedurla"
                >
                    <Input
                        type="number"
                        placeholder="(vuoto se non leggibile)"
                        value={form.vintage}
                        onChange={(e) => upd("vintage", e.target.value)}
                        className="bg-white"
                        data-testid="photo-field-vintage"
                    />
                </FormField>
                <FormField label="Tipologia">
                    <Select value={form.type} onValueChange={(v) => upd("type", v)}>
                        <SelectTrigger className="bg-white" data-testid="photo-field-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="rosso">Rosso</SelectItem>
                            <SelectItem value="bianco">Bianco</SelectItem>
                            <SelectItem value="rosato">Rosato</SelectItem>
                            <SelectItem value="bollicine">Bollicine</SelectItem>
                            <SelectItem value="dolce">Dolce</SelectItem>
                        </SelectContent>
                    </Select>
                </FormField>
                <FormField label="Paese">
                    <Input value={form.country} onChange={(e) => upd("country", e.target.value)} className="bg-white" data-testid="photo-field-country" />
                </FormField>
                <FormField label="Regione">
                    <Input value={form.region} onChange={(e) => upd("region", e.target.value)} className="bg-white" data-testid="photo-field-region" />
                </FormField>
                <FormField label="Provincia (opzionale)">
                    <Input value={form.provincia} onChange={(e) => upd("provincia", e.target.value)} className="bg-white" data-testid="photo-field-provincia" />
                </FormField>
                <FormField label="Denominazione">
                    <Input value={form.denomination} onChange={(e) => upd("denomination", e.target.value)} className="bg-white" data-testid="photo-field-denomination" />
                </FormField>
                <FormField label="Uvaggio (CSV)" hint="Es. Nebbiolo, Barbera">
                    <Input value={form.grapes_csv} onChange={(e) => upd("grapes_csv", e.target.value)} className="bg-white" data-testid="photo-field-grapes" />
                </FormField>
                <FormField label="Formato bottiglia">
                    <Select value={form.size} onValueChange={(v) => upd("size", v)}>
                        <SelectTrigger className="bg-white" data-testid="photo-field-size">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0.375">0,375 L (mezza)</SelectItem>
                            <SelectItem value="0.75">0,75 L (standard)</SelectItem>
                            <SelectItem value="1.5">1,5 L (Magnum)</SelectItem>
                        </SelectContent>
                    </Select>
                </FormField>
                <FormField label="Prezzo pubblico (€)">
                    <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={form.base_price_public}
                        onChange={(e) => upd("base_price_public", e.target.value)}
                        className="bg-white"
                        data-testid="photo-field-price"
                    />
                </FormField>
            </div>
            <div className="mt-5 pt-5 border-t border-[hsl(var(--border))] flex justify-end">
                <button
                    type="button"
                    onClick={onCreate}
                    disabled={creating}
                    className="co-btn-primary"
                    data-testid="photo-create-btn"
                >
                    <Check className={`w-4 h-4 ${creating ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {creating ? "Creazione…" : "Crea vino da foto"}
                </button>
            </div>
        </div>
    );
}

function FormField({ label, hint, required, children }) {
    return (
        <div>
            <Label className="co-caption mb-1 block">
                {label}
                {required && <span className="text-[hsl(var(--primary))] ml-1">*</span>}
            </Label>
            {children}
            {hint && <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{hint}</p>}
        </div>
    );
}
